/**
 * ReactionBar - Emoji reactions on photos
 *
 * Two modes:
 * - Full (photo viewer): Row of 5 emoji buttons, click to toggle
 * - Compact (timeline grid): Show only emojis with counts as overlay badge
 */
import { useReactions, useToggleReaction, type ReactionSummary } from '@wovendays/api-client';

const EMOJI_MAP: Record<string, string> = {
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  fire: '🔥',
};

const ALL_EMOJIS = Object.keys(EMOJI_MAP);

interface ReactionBarProps {
  mediaId?: string;
  reactions?: ReactionSummary[];
  compact?: boolean;
}

export function ReactionBar({ mediaId, reactions: prefetched, compact }: ReactionBarProps) {
  const { data } = useReactions(mediaId, prefetched);
  const { toggle } = useToggleReaction(mediaId);

  const reactionList = prefetched ?? data?.reactions ?? [];
  const reactionMap = new Map(reactionList.map(r => [r.emoji, r]));

  const handleToggle = (emoji: string) => {
    const r = reactionMap.get(emoji);
    toggle(emoji, !!r?.userReacted);
  };

  // Compact mode — overlay badge
  if (compact) {
    const active = reactionList.filter(r => r.count > 0);
    if (active.length === 0) return null;
    return (
      <div className="flex items-center gap-0.5 rounded-full bg-black/50 px-1.5 py-0.5">
        {active.map(r => (
          <span key={r.emoji} className="text-xs">
            {EMOJI_MAP[r.emoji]} <span className="font-mono text-white">{r.count}</span>
          </span>
        ))}
      </div>
    );
  }

  // Full mode — all 5 emoji buttons
  return (
    <div className="flex items-center gap-1">
      {ALL_EMOJIS.map(emoji => {
        const r = reactionMap.get(emoji);
        const active = r?.userReacted;
        return (
          <button
            key={emoji}
            onClick={() => handleToggle(emoji)}
            className={`flex items-center gap-0.5 rounded-full px-2 py-1 text-sm transition-colors ${
              active
                ? 'bg-white/20 ring-1 ring-white/40'
                : 'bg-white/10 hover:bg-white/20'
            }`}
            aria-label={`React with ${emoji}`}
          >
            <span>{EMOJI_MAP[emoji]}</span>
            {r && r.count > 0 && (
              <span className="font-mono text-xs text-white">{r.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
